import React, { createContext, FC, useState, useRef, PropsWithChildren, useEffect } from 'react';

import calculateRates from '../helpers/currency/calculateRates';
import useGeo, { GEO } from '../hooks/useGeo';
import getCurrencyRates from '../helpers/currency/getCurrencyRates';
import getCurrencyForCountry, { type CurrencyObject, SUPPORTED_CURRENCIES, USD_CURRENCY } from '../helpers/currency/getProductCurrency';

type TData = {
  geo: GEO | null;
  currency: CurrencyObject;
  currencyRate: number;
  rates: Record<string, number>;
  isRatesLoaded: boolean;
  hasRatesError: boolean;
  setCurrency: (currency: CurrencyObject) => void;
  updateCurrency: (currencyName: string) => void;
};

const RESTRICTED_COUNTRIES = ['RU'];
const DEFAULT_CURRENCY = getCurrencyForCountry('US');
const DEFAULT_RATES: Record<string, number> = { USD: 1 };
const CURRENCY_STORAGE_KEY = 'forestshop-currency';

export const CommonContext = createContext<TData>({
  geo: null,
  currency: DEFAULT_CURRENCY,
  currencyRate: DEFAULT_RATES.USD,
  rates: DEFAULT_RATES,
  isRatesLoaded: false,
  hasRatesError: false,
  setCurrency: () => {},
  updateCurrency: () => {},
});

const CommonProvider: FC<PropsWithChildren> = ({ children }) => {
  const geo = useGeo();
  const [currency, setCurrencyState] = useState<CurrencyObject>(DEFAULT_CURRENCY);
  const [rates, setRates] = useState<Record<string, number>>(DEFAULT_RATES);
  const [isRatesLoaded, setIsRatesLoaded] = useState(false);
  const [hasRatesError, setHasRatesError] = useState(false);
  const preferredCurrency = useRef<CurrencyObject | null>(null);

  const countryCode = geo?.country?.code;

  useEffect(() => {
    let isActive = true;

    try {
      const savedName = localStorage.getItem(CURRENCY_STORAGE_KEY);
      preferredCurrency.current = SUPPORTED_CURRENCIES.find((item) => item.name === savedName) || null;
    } catch {
      // Currency selection still works when browser storage is unavailable.
    }

    (async () => {
      try {
        const apiRates = await getCurrencyRates();
        const nextRates = calculateRates(apiRates);
        if (!isActive) return;
        setRates(nextRates);
        setIsRatesLoaded(true);
        setHasRatesError(false);
      } catch {
        if (isActive) setHasRatesError(true);
      }
    })();

    return () => { isActive = false; };
  }, []);

  useEffect(() => {
    const nextCurrency = preferredCurrency.current || getCurrencyForCountry(countryCode);
    setCurrencyState(rates[nextCurrency.name] > 0 ? nextCurrency : USD_CURRENCY);
  }, [countryCode, rates]);

  const setCurrency = (c: CurrencyObject) => {
    const selectedCurrency = SUPPORTED_CURRENCIES.find((item) => item.name === c.name);
    if (!selectedCurrency) return;
    preferredCurrency.current = selectedCurrency;
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, selectedCurrency.name);
    } catch {
      // Keep the current selection even when it cannot be persisted.
    }
    if (rates[selectedCurrency.name] > 0) setCurrencyState(selectedCurrency);
  };

  const updateCurrency = (currencyName: string) => {
    const selectedCurrency = SUPPORTED_CURRENCIES.find((item) => item.name === currencyName);
    if (selectedCurrency) setCurrency(selectedCurrency);
  };

  const content = RESTRICTED_COUNTRIES.includes(geo?.country?.code || '') ? (
    <h1>Sorry, this website is not available in your country.</h1>
  ) : (
    children
  );

  return (
    <CommonContext.Provider value={{ geo, currency, currencyRate: rates[currency.name], rates, isRatesLoaded, hasRatesError, setCurrency, updateCurrency }}>
      {content}
    </CommonContext.Provider>
  );
};

export default CommonProvider;
